import logger from 'ws-scripts/modules/logger';
import { calculateMonthlyPayment } from '../utils/calculations';
import {
	getRateForTerm,
	loadOemConfig,
	overrideInitialState,
	useOemSchedule
} from './oemOverrides';
import * as types from '../actions/types';

const initialState = {
	initial: {
		price: 0,
		cashPayment: 0,
		aprTerms: [],
		selectedAprTerm: -1,
		apr: 0
	},
	current: {
		downPayment: 0,
		selectedAprTerm: -1,
		apr: 0,
		monthlyPayment: 0
	},
	oemOverrides: {
		useOverrides: false,
		valid: false,
		schedule: {},
		defaultTerm: -1
	}
};

const SET_PREFS = 'WS_SCRIPTS_SET_PREFS';

export function execCalculateMonthlyPayment(state) {
	return calculateMonthlyPayment(
		state.initial.price,
		state.current.downPayment,
		state.current.apr,
		state.current.selectedAprTerm
	);
}

export function updateInitialDownPayment(state) {
	// make nit-picky linter happy
	const newState = state;

	if (!newState.initial.price) {
		newState.initial.price = 0;
	}

	if (typeof newState.initial.cashPaymentAbsolute !== 'undefined') {
		newState.initial.cashPayment = Math.min(
			newState.initial.cashPaymentAbsolute,
			newState.initial.price
		);
	} else if (typeof newState.initial.cashPaymentPercentage !== 'undefined') {
		// always round up
		const percentageDownPayment = Math.ceil(
			newState.initial.cashPaymentPercentage * newState.initial.price
		);

		newState.initial.cashPayment = Math.min(
			percentageDownPayment,
			newState.initial.price
		);
	} else {
		newState.initial.cashPayment = 0;
	}

	return newState;
}

export function handlePrefsUpdate(state, prefs) {
	// make nit-picky linter happy
	const newState = state;

	if (prefs.rate) {
		if (typeof prefs.rate === 'string') {
			newState.initial.apr = parseFloat(prefs.rate);
		} else {
			newState.initial.apr = prefs.rate;
		}
	}

	if (prefs.maxTerm && prefs.termInterval) {
		const tempTerms = [];
		let currTerm = prefs.maxTerm;
		let currInterval = prefs.termInterval;
		if (typeof currTerm === 'string') {
			currTerm = parseInt(currTerm, 10);
		}

		if (typeof currInterval === 'string') {
			currInterval = parseInt(currInterval, 10);
		}

		while (currTerm > 0) {
			tempTerms.push(currTerm);
			currTerm -= currInterval;
		}

		newState.initial.aprTerms = tempTerms.reverse();
	}

	if (prefs.selectedTerm && newState.initial.aprTerms.length > 0) {
		if (typeof prefs.selectedTerm === 'string') {
			newState.initial.selectedAprTerm = parseInt(prefs.selectedTerm, 10);
		} else {
			newState.initial.selectedAprTerm = prefs.selectedTerm;
		}
	}

	if (prefs.cashPayment) {
		const numberStr = prefs.cashPayment.replace(/[^0-9.]/g, '');

		// If cashPayment pref begins with '$' it is treated as a flat dollar amount.
		// If cashPayment pref ends with '%' it is treated as a percentage.
		// If neither is present then value is set to 0.

		if (prefs.cashPayment.startsWith('$')) {
			newState.initial.cashPaymentAbsolute = parseFloat(numberStr);
			delete newState.initial.cashPaymentPercentage;
		} else if (prefs.cashPayment.endsWith('%')) {
			newState.initial.cashPaymentPercentage =
				parseFloat(numberStr) / 100;
			delete newState.initial.cashPaymentAbsolute;
		}
	}

	if (prefs.useOemAprTermSchedule === 'true') {
		newState.oemOverrides.useOverrides = true;
	} else {
		newState.oemOverrides.useOverrides = false;
	}

	if (useOemSchedule(newState.oemOverrides)) {
		overrideInitialState(newState);
	}

	return newState;
}

export function resetCalculations(state) {
	const newState = state;
	newState.current.apr = newState.initial.apr;
	newState.current.selectedAprTerm = newState.initial.selectedAprTerm;

	newState.current.downPayment = newState.initial.cashPayment;

	newState.current.monthlyPayment = execCalculateMonthlyPayment(newState);
	return newState;
}

function copyState(state) {
	return {
		initial: { ...state.initial },
		current: { ...state.current },
		oemOverrides: { ...state.oemOverrides }
	};
}

export default function calculatorData(state, action) {
	if (typeof state === 'undefined') {
		return initialState;
	}

	let newState;
	let newVal;
	if (!action.error) {
		switch (action.type) {
			case types.LOAD_PRICE:
				newState = copyState(state);
				newState.initial.price = action.payload.price;
				newState = updateInitialDownPayment(newState);
				newState = resetCalculations(newState);
				break;
			case types.LOAD_OEM_CALCULATOR_CONFIG:
				newState = copyState(state);
				// Verify OEM overrides for CALCULATOR CONFIG & Insert & check pref
				newState = loadOemConfig(newState, action.payload);
				newState = resetCalculations(newState);
				break;
			case SET_PREFS:
				newState = copyState(state);
				newState = handlePrefsUpdate(state, action.payload);
				newState = updateInitialDownPayment(newState);
				newState = resetCalculations(newState);
				break;
			case types.UPDATE_DOWN_PAYMENT:
				newState = copyState(state);
				// handle value edge cases
				newVal = Math.min(
					action.payload.downPayment,
					newState.initial.price
				);
				newVal = Math.max(newVal, 0);

				newState.current.downPayment = newVal;
				newState.current.monthlyPayment = execCalculateMonthlyPayment(
					newState
				);

				// console.log(JSON.stringify(newState,null,2));
				break;
			case types.UPDATE_APR:
				newState = copyState(state);
				if (action.payload.apr < 0) {
					newVal = 0.0;
				} else {
					newVal = action.payload.apr;
				}

				newState.current.apr = newVal;
				newState.current.monthlyPayment = execCalculateMonthlyPayment(
					newState
				);
				break;
			case types.UPDATE_SELECTED_APR_TERM:
				newState = copyState(state);

				newState.current.selectedAprTerm = action.payload.term;

				if (useOemSchedule(newState.oemOverrides)) {
					try {
						newState.current.apr = getRateForTerm(
							action.payload.term,
							newState.oemOverrides
						);
					} catch (e) {
						logger.error(
							'Unable to set APR for selected term',
							e.message
						);
					}
				}

				newState.current.monthlyPayment = execCalculateMonthlyPayment(
					newState
				);
				break;
			case types.RESET_CALCULATOR:
				newState = copyState(state);
				resetCalculations(newState);
				break;

			default:
				newState = state;
				break;
		}
	}

	return newState;
}



// WEBPACK FOOTER //
// ./src/reducers/calculatorData.js